import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { css } from '@emotion/css';
import { Button, Input, Typography, theme } from 'antd';
// Internal path: antd does not re-export it. Clearing the status context is how a custom
// form control keeps a Form.Item error from painting every input inside it red.
import { FormItemInputContext } from 'antd/es/form/context';
import type React from 'react';
import { useState } from 'react';
import { LazySortableRows } from '../../../../components/LazySortableRows.tsx';
import { useRowKeys } from '../../../../hooks/useRowKeys.ts';
import { MAX_LOCALIZATION_LENGTH } from '../../../../utils/localizationLimits.ts';

/**
 * Ordered list of predefined Lokalisationen. The list order IS the anatomical order that the
 * request UI and the Schein render, so it is drag-sortable rather than a tag cloud.
 */
export const PredefinedLocalizationsEditor: React.FC<{
  value?: string[];
  onChange?: (value: string[]) => void;
}> = ({ value = [], onChange }) => {
  const { token } = theme.useToken();
  const [draft, setDraft] = useState('');
  const rowKeys = useRowKeys(value.length);

  const add = () => {
    const name = draft.trim();
    if (!name || value.includes(name)) {
      return;
    }
    onChange?.([...value, name]);
    setDraft('');
  };

  const rename = (index: number, name: string) => onChange?.(value.map((it, i) => (i === index ? name : it)));

  const remove = (index: number) => {
    rowKeys.remove(index);
    onChange?.(value.filter((_, i) => i !== index));
  };

  const listStyle = css`
    margin-bottom: ${token.marginXS}px;
  `;

  const addRowStyle = css`
    display: flex;
    gap: ${token.marginXXS}px;
  `;

  const hintStyle = css`
    display: block;
    margin-bottom: ${token.marginXS}px;
    font-size: ${token.fontSizeSM}px;
  `;

  /** Trimmed, like the server: two entries differing only in whitespace look identical here. */
  const duplicate = (name: string, index: number) => value.findIndex(it => it.trim() === name.trim()) !== index;

  const invalidName = (name: string, index: number) => !name.trim() || duplicate(name, index);

  return (
    <div>
      <Typography.Text type="secondary" className={hintStyle}>
        Die Reihenfolge der Liste ist die anatomische Reihenfolge.
      </Typography.Text>
      <div className={listStyle}>
        <FormItemInputContext.Provider value={{}}>
          <LazySortableRows
            items={value}
            keyOf={(_, index) => rowKeys.keyAt(index)}
            onReorder={(items, from, to) => {
              rowKeys.move(from, to);
              onChange?.(items);
            }}
            renderRow={(name, index) => (
              <>
                <Input
                  size="small"
                  value={name}
                  maxLength={MAX_LOCALIZATION_LENGTH}
                  status={invalidName(name, index) ? 'error' : undefined}
                  onChange={e => rename(index, e.target.value)}
                />
                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(index)} />
              </>
            )}
          />
        </FormItemInputContext.Provider>
      </div>
      <FormItemInputContext.Provider value={{}}>
        <div className={addRowStyle}>
          <Input
            size="small"
            placeholder="Neue Lokalisation"
            value={draft}
            maxLength={MAX_LOCALIZATION_LENGTH}
            onChange={e => setDraft(e.target.value)}
            onPressEnter={e => {
              e.preventDefault();
              add();
            }}
          />
          <Button size="small" icon={<PlusOutlined />} onClick={add} disabled={!draft.trim()}>
            Hinzufügen
          </Button>
        </div>
      </FormItemInputContext.Provider>
    </div>
  );
};
